import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Refresh as RefreshIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useAuth } from '@clerk/clerk-react';
import { defaultConfig } from '../config/bundlerConfig';

async function apiFetchAdmin(getToken, path, init = {}) {
  const adminEmail = localStorage.getItem('adminEmail') || '';
  const adminPassword = localStorage.getItem('adminPassword') || '';
  let token = null;
  try {
    token = await getToken();
  } catch {
    token = null;
  }
  const res = await fetch(`${defaultConfig.SERVER_URL.url}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(adminEmail && adminPassword ? { 'X-Admin-Email': adminEmail, 'X-Admin-Password': adminPassword } : {}),
    },
  });
  const text = await res.text();
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = {};
  }
  if (!res.ok) throw new Error(data?.error || `Request failed (${res.status})`);
  return data;
}

export default function AdminPanel() {
  const { getToken } = useAuth();
  const [isAdmin, setIsAdmin] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [adminEmail, setAdminEmail] = useState(() => localStorage.getItem('adminEmail') || 'admin@admin.com');
  const [adminPassword, setAdminPassword] = useState(() => localStorage.getItem('adminPassword') || '1234');

  const [billing, setBilling] = useState({
    enabled: true,
    amountTokens: 600,
    tokenSymbol: 'USDT',
    tokenAddress: '',
    tokenDecimals: 6,
    chainId: 1,
    treasuryAddress: '',
    rpcUrl: '',
  });

  const [platform, setPlatform] = useState({
    rpcUrl: '',
    chainId: 1,
    nativeSymbol: 'ETH',
    uniswapV2Router: '',
    wethAddress: '',
    disperseAddress: '',
    bundleRelay: 'blockrazor',
    flashbotsUrl: 'https://relay.flashbots.net',
    blockrazorUrl: 'https://eth.blockrazor.xyz',
  });
  const [platformSavedAt, setPlatformSavedAt] = useState(0);
  const [whitelist, setWhitelist] = useState([]);
  const [newWhitelistUserId, setNewWhitelistUserId] = useState('');
  const [newWhitelistNote, setNewWhitelistNote] = useState('');

  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [query, setQuery] = useState('');

  const [overview, setOverview] = useState(null);
  const [loadingOverview, setLoadingOverview] = useState(false);

  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => (u.email || '').toLowerCase().includes(q) || (u.id || '').toLowerCase().includes(q));
  }, [users, query]);

  const loadIsAdmin = async () => {
    try {
      const data = await apiFetchAdmin(getToken, '/api/admin/me');
      setIsAdmin(!!data?.isAdmin);
    } catch (e) {
      setIsAdmin(false);
    }
  };

  const handleAdminLogin = async () => {
    localStorage.setItem('adminEmail', adminEmail.trim());
    localStorage.setItem('adminPassword', adminPassword);
    setError('');
    try {
      const data = await apiFetchAdmin(getToken, '/api/admin/me');
      setIsAdmin(!!data?.isAdmin);
      if (!data?.isAdmin) setError('Invalid admin credentials.');
    } catch (e) {
      setIsAdmin(false);
      setError(e.message || 'Invalid admin credentials.');
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiFetchAdmin(getToken, '/api/admin/users?limit=100&offset=0');
      const rows = data.users;
      const list = Array.isArray(rows) ? rows : [];
      setUsers(list);
      if (!selectedUserId && list[0]?.id) setSelectedUserId(list[0].id);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  const loadBilling = async () => {
    try {
      const data = await apiFetchAdmin(getToken, '/api/admin/billing/settings');
      setBilling((prev) => ({ ...prev, ...(data.settings || {}) }));
    } catch (e) {
      // ignore until configured
    }
  };

  const saveBilling = async () => {
    setLoading(true);
    setError('');
    try {
      await apiFetchAdmin(getToken, '/api/admin/billing/settings', {
        method: 'PUT',
        body: JSON.stringify(billing),
      });
      await loadBilling();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  const loadPlatform = async () => {
    try {
      const data = await apiFetchAdmin(getToken, '/api/admin/platform/settings');
      setPlatform((prev) => ({ ...prev, ...(data.settings || {}) }));
    } catch (e) {
      // ignore until configured
    }
  };

  const savePlatform = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiFetchAdmin(getToken, '/api/admin/platform/settings', {
        method: 'PUT',
        body: JSON.stringify(platform),
      });
      setPlatform((prev) => ({ ...prev, ...(data.settings || {}) }));
      setPlatformSavedAt(Date.now());
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  const loadWhitelist = async () => {
    try {
      const data = await apiFetchAdmin(getToken, '/api/admin/billing/whitelist');
      const wl = data.whitelist;
      setWhitelist(Array.isArray(wl) ? wl : []);
    } catch (e) {
      // ignore
    }
  };

  const addWhitelist = async () => {
    const userId = newWhitelistUserId.trim();
    if (!userId) return;
    setLoading(true);
    setError('');
    try {
      await apiFetchAdmin(getToken, '/api/admin/billing/whitelist', {
        method: 'POST',
        body: JSON.stringify({ userId, note: newWhitelistNote.trim() }),
      });
      setNewWhitelistUserId('');
      setNewWhitelistNote('');
      await loadWhitelist();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  const removeWhitelist = async (userId) => {
    setLoading(true);
    setError('');
    try {
      await apiFetchAdmin(getToken, `/api/admin/billing/whitelist/${encodeURIComponent(userId)}`, { method: 'DELETE' });
      await loadWhitelist();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  const loadOverview = async (userId) => {
    if (!userId) return;
    setLoadingOverview(true);
    setError('');
    try {
      const data = await apiFetchAdmin(getToken, `/api/admin/users/${encodeURIComponent(userId)}/overview`);
      setOverview({
        ...data,
        projects: Array.isArray(data?.projects) ? data.projects : [],
        payments: Array.isArray(data?.payments) ? data.payments : [],
        activities: Array.isArray(data?.activities) ? data.activities : [],
      });
    } catch (e) {
      setOverview(null);
      setError(e.message || String(e));
    } finally {
      setLoadingOverview(false);
    }
  };

  useEffect(() => {
    loadIsAdmin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAdmin) {
      loadUsers();
      loadBilling();
      loadPlatform();
      loadWhitelist();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin && selectedUserId) loadOverview(selectedUserId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, selectedUserId]);

  const handleRestrict = async ({ banned, locked }) => {
    if (!selectedUserId) return;
    setLoadingOverview(true);
    setError('');
    try {
      await apiFetchAdmin(getToken, `/api/admin/users/${encodeURIComponent(selectedUserId)}/restrict`, {
        method: 'POST',
        body: JSON.stringify({ banned, locked }),
      });
      await loadOverview(selectedUserId);
      await loadUsers();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoadingOverview(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUserId) return;
    const ok = window.confirm('Delete this user account? This cannot be undone.');
    if (!ok) return;
    setLoadingOverview(true);
    setError('');
    try {
      await apiFetchAdmin(getToken, `/api/admin/users/${encodeURIComponent(selectedUserId)}`, { method: 'DELETE' });
      setSelectedUserId('');
      setOverview(null);
      await loadUsers();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoadingOverview(false);
    }
  };

  if (isAdmin === false) {
    return (
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 900 }}>
          Admin
        </Typography>
        <Card variant="outlined" sx={{ mt: 2, maxWidth: 520 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 850 }}>
              Admin login
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Enter your admin email and password.
            </Typography>
            <Stack spacing={1.25} sx={{ mt: 2 }}>
              <TextField
                size="small"
                label="Admin email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                autoComplete="username"
              />
              <TextField
                size="small"
                label="Admin password"
                type="password"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                autoComplete="current-password"
              />
              <Button variant="contained" onClick={handleAdminLogin} sx={{ borderRadius: 999, alignSelf: 'flex-start' }}>
                Login
              </Button>
              {error ? <Alert severity="error">{error}</Alert> : null}
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', mb: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900 }}>
            Admin Panel
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            View users, review activity, restrict access, or delete accounts.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <IconButton onClick={() => loadUsers()} disabled={loading} size="small">
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                Platform settings
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                The RPC and chain the React app uses for every user. Saved to{' '}
                <code>config.json</code> and pushed to the browser via{' '}
                <code>/runtime-config.js</code>. Reload the page after saving.
              </Typography>
              <Divider sx={{ my: 1.5 }} />
              <Grid container spacing={1.25}>
                <Grid item xs={12} md={8}>
                  <TextField
                    label="Platform RPC URL"
                    value={platform.rpcUrl}
                    onChange={(e) => setPlatform((p) => ({ ...p, rpcUrl: e.target.value }))}
                    placeholder="https://eth.llamarpc.com"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    label="Chain ID"
                    value={platform.chainId}
                    onChange={(e) => setPlatform((p) => ({ ...p, chainId: e.target.value }))}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    label="Native symbol"
                    value={platform.nativeSymbol}
                    onChange={(e) => setPlatform((p) => ({ ...p, nativeSymbol: e.target.value }))}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Uniswap V2 router"
                    value={platform.uniswapV2Router}
                    onChange={(e) => setPlatform((p) => ({ ...p, uniswapV2Router: e.target.value }))}
                    placeholder="0x7a25..."
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="WETH address"
                    value={platform.wethAddress}
                    onChange={(e) => setPlatform((p) => ({ ...p, wethAddress: e.target.value }))}
                    placeholder="0xC02a..."
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Disperse contract (native transfers)"
                    value={platform.disperseAddress}
                    onChange={(e) => setPlatform((p) => ({ ...p, disperseAddress: e.target.value }))}
                    placeholder="0xD152... (Disperse.app)"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel id="bundle-relay-label">Bundle relay</InputLabel>
                    <Select
                      labelId="bundle-relay-label"
                      label="Bundle relay"
                      value={platform.bundleRelay || 'blockrazor'}
                      onChange={(e) => setPlatform((p) => ({ ...p, bundleRelay: e.target.value }))}
                    >
                      <MenuItem value="blockrazor">BlockRazor (default)</MenuItem>
                      <MenuItem value="flashbots">Flashbots / MEV-Share (via API)</MenuItem>
                      <MenuItem value="disabled">Disabled</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="BlockRazor RPC URL"
                    value={platform.blockrazorUrl || ''}
                    onChange={(e) => setPlatform((p) => ({ ...p, blockrazorUrl: e.target.value }))}
                    placeholder="https://eth.blockrazor.xyz (Ethereum) or BSC gateway from BlockRazor docs"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Flashbots URL (only if relay = Flashbots)"
                    value={platform.flashbotsUrl}
                    onChange={(e) => setPlatform((p) => ({ ...p, flashbotsUrl: e.target.value }))}
                    placeholder="https://relay.flashbots.net"
                    fullWidth
                  />
                </Grid>
              </Grid>
              {platformSavedAt ? (
                <Alert severity="success" sx={{ mt: 1.5 }}>
                  Saved. Reload the page (Cmd/Ctrl+R) so users on this server pick up the new RPC.
                </Alert>
              ) : null}
              <Stack direction="row" spacing={1} sx={{ mt: 1.5, justifyContent: 'flex-end' }}>
                <Button variant="outlined" onClick={loadPlatform} disabled={loading}>
                  Reset
                </Button>
                <Button variant="contained" onClick={savePlatform} disabled={loading}>
                  Save
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                Billing settings
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Configure payment per launch (per project) and the USDT token/treasury addresses.
              </Typography>
              <Divider sx={{ my: 1.5 }} />
              <Grid container spacing={1.25}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Amount"
                    value={billing.amountTokens}
                    onChange={(e) => setBilling((p) => ({ ...p, amountTokens: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Token symbol"
                    value={billing.tokenSymbol}
                    onChange={(e) => setBilling((p) => ({ ...p, tokenSymbol: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Decimals"
                    value={billing.tokenDecimals}
                    onChange={(e) => setBilling((p) => ({ ...p, tokenDecimals: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="USDT token address"
                    value={billing.tokenAddress}
                    onChange={(e) => setBilling((p) => ({ ...p, tokenAddress: e.target.value }))}
                    placeholder="0x..."
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Treasury address"
                    value={billing.treasuryAddress}
                    onChange={(e) => setBilling((p) => ({ ...p, treasuryAddress: e.target.value }))}
                    placeholder="0x..."
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Chain ID"
                    value={billing.chainId}
                    onChange={(e) => setBilling((p) => ({ ...p, chainId: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField
                    label="RPC URL"
                    value={billing.rpcUrl}
                    onChange={(e) => setBilling((p) => ({ ...p, rpcUrl: e.target.value }))}
                    placeholder="https://..."
                    fullWidth
                  />
                </Grid>
              </Grid>
              <Stack direction="row" spacing={1} sx={{ mt: 1.5, justifyContent: 'flex-end' }}>
                <Button variant="outlined" onClick={loadBilling} disabled={loading}>
                  Reset
                </Button>
                <Button variant="contained" onClick={saveBilling} disabled={loading}>
                  Save
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                Whitelist (no payment)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Add Clerk user IDs to allow access without payment.
              </Typography>
              <Divider sx={{ my: 1.5 }} />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { sm: 'center' } }}>
                <TextField
                  label="User ID"
                  value={newWhitelistUserId}
                  onChange={(e) => setNewWhitelistUserId(e.target.value)}
                  placeholder="user_..."
                  fullWidth
                />
                <TextField
                  label="Note (optional)"
                  value={newWhitelistNote}
                  onChange={(e) => setNewWhitelistNote(e.target.value)}
                  fullWidth
                />
                <Button variant="contained" onClick={addWhitelist} disabled={loading} sx={{ whiteSpace: 'nowrap' }}>
                  Add
                </Button>
              </Stack>

              <Divider sx={{ my: 1.5 }} />

              <Stack spacing={1} sx={{ maxHeight: 260, overflow: 'auto' }}>
                {(whitelist || []).map((w) => (
                  <Box key={w.user_id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 750 }} noWrap>
                        {w.user_id}
                      </Typography>
                      {w.note ? (
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {w.note}
                        </Typography>
                      ) : null}
                    </Box>
                    <Button variant="outlined" color="error" onClick={() => removeWhitelist(w.user_id)} disabled={loading}>
                      Remove
                    </Button>
                  </Box>
                ))}
                {(whitelist || []).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No whitelisted users yet.
                  </Typography>
                ) : null}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={4}>
          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 850 }}>
                  Users
                </Typography>
                <Chip size="small" label={`${users.length}`} />
              </Stack>

              <TextField
                size="small"
                fullWidth
                placeholder="Search email or user id"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />

              <Divider sx={{ my: 1.5 }} />

              <List dense sx={{ maxHeight: 520, overflow: 'auto' }}>
                {filteredUsers.map((u) => (
                  <ListItem key={u.id} disablePadding>
                    <ListItemButton selected={selectedUserId === u.id} onClick={() => setSelectedUserId(u.id)}>
                      <ListItemText
                        primary={u.email || u.id}
                        secondary={
                          <span>
                            {u.banned ? 'BANNED · ' : ''}
                            {u.locked ? 'LOCKED · ' : ''}
                            {u.id}
                          </span>
                        }
                        primaryTypographyProps={{ sx: { fontWeight: 750, fontSize: 13.5 } }}
                        secondaryTypographyProps={{ sx: { color: 'text.secondary', fontSize: 12 } }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card variant="outlined">
            <CardContent>
              {!selectedUserId ? (
                <Alert severity="info">Select a user.</Alert>
              ) : loadingOverview ? (
                <Alert severity="info">Loading user…</Alert>
              ) : !overview ? (
                <Alert severity="warning">No details available.</Alert>
              ) : (
                <>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 900 }}>
                        {overview.user?.email || overview.user?.id}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        {overview.user?.id}
                      </Typography>
                    </Box>

                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                      <Chip size="small" label={overview.user?.banned ? 'Banned' : 'Not banned'} color={overview.user?.banned ? 'error' : 'default'} />
                      <Chip size="small" label={overview.user?.locked ? 'Locked' : 'Not locked'} color={overview.user?.locked ? 'warning' : 'default'} />
                      <IconButton onClick={handleDeleteUser} size="small" color="error" title="Delete user">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>

                  <Divider sx={{ my: 2 }} />

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { sm: 'center' } }}>
                    <Button
                      variant="outlined"
                      onClick={() => handleRestrict({ banned: !overview.user?.banned })}
                    >
                      {overview.user?.banned ? 'Unban user' : 'Ban user'}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => handleRestrict({ locked: !overview.user?.locked })}
                    >
                      {overview.user?.locked ? 'Unlock user' : 'Lock user'}
                    </Button>
                  </Stack>

                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
                        <CardContent>
                          <Typography variant="subtitle2" sx={{ fontWeight: 850 }}>
                            Projects
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                            {overview.projects?.length || 0} total
                          </Typography>
                          <Divider sx={{ my: 1.25 }} />
                          <Stack spacing={1}>
                            {(overview.projects || []).slice(0, 10).map((p) => (
                              <Box key={p.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {p.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {p.id}
                                </Typography>
                              </Box>
                            ))}
                            {(overview.projects || []).length > 10 ? (
                              <Typography variant="caption" color="text.secondary">
                                Showing first 10…
                              </Typography>
                            ) : null}
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
                        <CardContent>
                          <Typography variant="subtitle2" sx={{ fontWeight: 850 }}>
                            Payments
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                            {overview.payments?.length || 0} records
                          </Typography>
                          <Divider sx={{ my: 1.25 }} />
                          <Stack spacing={1}>
                            {(overview.payments || []).slice(0, 10).map((p) => (
                              <Box key={`${p.project_id}.${p.tx_hash}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {p.status} · {String(p.amount_wei || '').slice(0, 10)}…
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {p.project_id}
                                </Typography>
                              </Box>
                            ))}
                            {(overview.payments || []).length > 10 ? (
                              <Typography variant="caption" color="text.secondary">
                                Showing first 10…
                              </Typography>
                            ) : null}
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  <Card variant="outlined" sx={{ mt: 2, borderRadius: 2.5 }}>
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ fontWeight: 850 }}>
                        Recent activity
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        {(overview.activities || []).length} events (max 200)
                      </Typography>
                      <Divider sx={{ my: 1.25 }} />
                      <Stack spacing={1} sx={{ maxHeight: 300, overflow: 'auto' }}>
                        {(overview.activities || []).map((a, idx) => (
                          <Box key={`${a.createdAt}.${idx}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 750 }}>
                                {a.action}
                              </Typography>
                              {a.projectId ? (
                                <Typography variant="caption" color="text.secondary">
                                  project: {a.projectId}
                                </Typography>
                              ) : null}
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                              {a.createdAt}
                            </Typography>
                          </Box>
                        ))}
                        {(overview.activities || []).length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            No activity logs yet.
                          </Typography>
                        ) : null}
                      </Stack>
                    </CardContent>
                  </Card>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

